/** Maximum number of excluded values that triggers the "all except" form. */
export const ALL_EXCEPT_THRESHOLD = 3;

/**
 * Collapses a multi-select selection into a compact chip value:
 *
 *   selected.length === options.length          → "all"
 *   1..ALL_EXCEPT_THRESHOLD excluded             → "all except A, B, C"
 *     (and excluded.length < selected.length, so listing the excluded
 *      is genuinely shorter than listing the selected)
 *   otherwise                                    → "A, B, C, ..." (joined selected)
 *
 * If options is missing/empty, falls back to comma-joined selected values
 * (legacy behavior, preserves back-compat for callers that don't yet pass
 * the option pool).
 */
export const formatMultiSelectValue = (
  selected: string[],
  options: string[] | undefined
): string => {
  if (selected.length === 0) return '';
  if (!options || options.length === 0) return selected.join(', ');

  // Sanity guard: if the selection contains values that aren't in the current
  // option pool, the pool is stale or out-of-context (e.g. data turnover
  // between sessions). Don't claim "all" / "all except" — fall back to listing
  // the actual selected values.
  const optionSet = new Set(options);
  if (!selected.every((v) => optionSet.has(v))) return selected.join(', ');

  if (selected.length === options.length) return 'all';

  const excluded = options.filter((o) => !selected.includes(o));
  const excludedFitsThreshold = excluded.length > 0 && excluded.length <= ALL_EXCEPT_THRESHOLD;
  // Don't claim "all except" when the selected list itself is shorter — listing
  // the selected values (e.g. "A") is more compact than "all except B, C, D".
  const excludedIsShorter = excluded.length < selected.length;
  if (excludedFitsThreshold && excludedIsShorter) {
    return `all except ${excluded.join(', ')}`;
  }
  return selected.join(', ');
};
